// Braden and Morse instrument definitions, BSA, and the risk cut-offs.
//
// These mirror main/ipd/nursing_scores.py. The BACKEND is the authority: it
// revalidates every subscale, recomputes the total, and derives the risk level
// that gets stored. What lives here is the option wording the form renders plus
// a local total/level so the nurse sees the score update as they answer — a UI
// echo, never the stored value. If the two ever disagree, the backend is right.
//
// They are fixed published instruments (Braden 1987, Morse 1989) — the options,
// weights and cut-offs are not facility-configurable.

export const BRADEN_SUBSCALES = [
  {
    key: "sensory_perception",
    label: "Sensory perception",
    help: "Ability to respond meaningfully to pressure-related discomfort",
    options: [
      { value: 1, label: "Completely limited" },
      { value: 2, label: "Very limited" },
      { value: 3, label: "Slightly limited" },
      { value: 4, label: "No impairment" },
    ],
  },
  {
    key: "moisture",
    label: "Moisture",
    help: "Degree to which skin is exposed to moisture",
    options: [
      { value: 1, label: "Constantly moist" },
      { value: 2, label: "Very moist" },
      { value: 3, label: "Occasionally moist" },
      { value: 4, label: "Rarely moist" },
    ],
  },
  {
    key: "activity",
    label: "Activity",
    help: "Degree of physical activity",
    options: [
      { value: 1, label: "Bedfast" },
      { value: 2, label: "Chairfast" },
      { value: 3, label: "Walks occasionally" },
      { value: 4, label: "Walks frequently" },
    ],
  },
  {
    key: "mobility",
    label: "Mobility",
    help: "Ability to change and control body position",
    options: [
      { value: 1, label: "Completely immobile" },
      { value: 2, label: "Very limited" },
      { value: 3, label: "Slightly limited" },
      { value: 4, label: "No limitations" },
    ],
  },
  {
    key: "nutrition",
    label: "Nutrition",
    help: "Usual food intake pattern",
    options: [
      { value: 1, label: "Very poor" },
      { value: 2, label: "Probably inadequate" },
      { value: 3, label: "Adequate" },
      { value: 4, label: "Excellent" },
    ],
  },
  {
    // Friction/shear scores 1-3, not 1-4 — the one asymmetric subscale.
    key: "friction_shear",
    label: "Friction & shear",
    options: [
      { value: 1, label: "Problem" },
      { value: 2, label: "Potential problem" },
      { value: 3, label: "No apparent problem" },
    ],
  },
];

// Morse items are WEIGHTED, not ordinal: the option values are the points.
export const MORSE_ITEMS = [
  {
    key: "history_of_falling",
    label: "History of falling",
    help: "Fell during this admission, or within the last 3 months",
    options: [{ value: 0, label: "No" }, { value: 25, label: "Yes" }],
  },
  {
    key: "secondary_diagnosis",
    label: "Secondary diagnosis",
    help: "More than one medical diagnosis on the chart",
    options: [{ value: 0, label: "No" }, { value: 15, label: "Yes" }],
  },
  {
    key: "ambulatory_aid",
    label: "Ambulatory aid",
    options: [
      { value: 0, label: "None / bedrest / nurse assist" },
      { value: 15, label: "Crutches / cane / walker" },
      { value: 30, label: "Furniture" },
    ],
  },
  {
    key: "iv_access",
    label: "IV access / heparin lock",
    options: [{ value: 0, label: "No" }, { value: 20, label: "Yes" }],
  },
  {
    key: "gait",
    label: "Gait",
    options: [
      { value: 0, label: "Normal / bedrest / wheelchair" },
      { value: 10, label: "Weak" },
      { value: 20, label: "Impaired" },
    ],
  },
  {
    key: "mental_status",
    label: "Mental status",
    options: [
      { value: 0, label: "Oriented to own ability" },
      { value: 15, label: "Overestimates / forgets limitations" },
    ],
  },
];

export const BRADEN_MIN = 6;
export const BRADEN_MAX = 23;
export const MORSE_MIN = 0;
export const MORSE_MAX = 125;

// Sum the answers, or null when the instrument is not fully answered — a total
// from a partial instrument is not a score, and the backend rejects one.
export function scoreTotal(answers, definition) {
  if (!answers) return null;
  const values = definition.map((d) => answers[d.key]);
  if (values.some((v) => v === undefined || v === null || v === "")) return null;
  return values.reduce((sum, v) => sum + Number(v), 0);
}

// Lower Braden = higher risk; higher Morse = higher risk.
export function bradenLevel(total) {
  if (total == null) return null;
  if (total >= 19) return "Low";
  if (total >= 13) return "Moderate";
  return "High";
}

export function morseLevel(total) {
  if (total == null) return null;
  if (total >= 45) return "High";
  if (total >= 25) return "Moderate";
  return "Low";
}

// Score bands for the visual bars, in ascending score order.
export const BRADEN_BANDS = [
  { upTo: 12, level: "High" },
  { upTo: 18, level: "Moderate" },
  { upTo: BRADEN_MAX, level: "Low" },
];
export const MORSE_BANDS = [
  { upTo: 24, level: "Low" },
  { upTo: 44, level: "Moderate" },
  { upTo: MORSE_MAX, level: "High" },
];
// Pain has no published tri-band; these are the conventional mild/moderate/severe splits.
export const PAIN_BANDS = [
  { upTo: 3, level: "Low" },
  { upTo: 6, level: "Moderate" },
  { upTo: 10, level: "High" },
];

// Body Surface Area (Mosteller): sqrt(height_cm * weight_kg / 3600), m².
// Derived on render and never stored, so it cannot contradict the height and
// weight it comes from (CONTEXT.md "BSA").
export function mostellerBsa(heightCm, weightKg) {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!h || !w || h <= 0 || w <= 0) return null;
  return Math.sqrt((h * w) / 3600);
}

export const O2_MODES = [
  "Room Air", "Nasal Cannula", "Face Mask", "NRB", "HFNC", "NIV", "Ventilator",
];
export const O2_MODE_ROOM_AIR = "Room Air";

export const HISTORY_SECTIONS = [
  { key: "past_medical", label: "Past medical history" },
  { key: "past_surgical", label: "Past surgical history" },
  { key: "family", label: "Family history" },
  { key: "social", label: "Personal / social history" },
];
